import CommentPolicyHint from "../components/CommentPolicyHint";
import { usePermission } from "../hooks/usePermission";
import React, { useEffect, useState, useMemo } from "react";
import api from "../api/api";
import { SizeBadge, calcTicketSize } from "../utils/sizing";
import toast from "react-hot-toast";
import {
  Plus, Search, X, Pencil, Trash2, Clock, Hash,
  Building2, User, Calendar, Filter, XCircle, ChevronDown, ExternalLink, Users as UsersIcon, UserPlus, Send, MessageSquare,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const fmtMin = (m: number) => { const h = Math.floor(m/60); return `${h}:${String(m%60).padStart(2,'0')}`; };

const parseToMinutes = (val: string, unit: string): number => {
  const s = (val || "").trim();
  const hm = s.match(/^(\d+(?:\.\d+)?)h\s*(\d+)m?$/i);
  if (hm) return Math.max(1, Math.round(parseFloat(hm[1]) * 60 + parseInt(hm[2])));
  const h = s.match(/^(\d+(?:\.\d+)?)h$/i);
  if (h) return Math.max(1, Math.round(parseFloat(h[1]) * 60));
  const m = s.match(/^(\d+)m$/i);
  if (m) return Math.max(1, parseInt(m[1]));
  const n = parseFloat(s);
  if (isNaN(n) || n <= 0) return 0;
  return unit === "hr" ? Math.max(1, Math.round(n * 60)) : Math.max(1, Math.round(n));
};


/* ── constants ──────────────────────────────────────────────── */
const STATUS_OPTIONS = ["OPEN","ANSWERED","IN_PROGRESS","ON_HOLD","CLOSED"];
const TYPE_OPTIONS   = ["REQUEST","WEBSITE_ISSUE","EMAIL_ISSUE","SSL","COLLECTION","OTHER"];
const DEPARTMENTS    = ["Technical Support","Development","Customer Support","Billing","Other"];

const TYPE_LABEL: Record<string,string> = {
  REQUEST:"Request", WEBSITE_ISSUE:"Website Issue",
  EMAIL_ISSUE:"Email Issue", SSL:"SSL", COLLECTION:"Collection", OTHER:"Other",
};

const STATUS_COLORS: Record<string,string> = {
  OPEN:"bg-red-100 text-red-700", ANSWERED:"bg-blue-100 text-blue-700",
  IN_PROGRESS:"bg-yellow-100 text-yellow-700", ON_HOLD:"bg-gray-100 text-gray-600",
  CLOSED:"bg-green-100 text-green-700",
};
const TYPE_COLORS: Record<string,string> = {
  REQUEST:"bg-purple-100 text-purple-700", WEBSITE_ISSUE:"bg-orange-100 text-orange-700",
  EMAIL_ISSUE:"bg-pink-100 text-pink-700", SSL:"bg-indigo-100 text-indigo-700", OTHER:"bg-gray-100 text-gray-700",
  COLLECTION:"bg-teal-100 text-teal-700",
};

const blankForm = {
  ticketNumber:"", referenceLink:"", initialComment:"",
  department:"", customDepartment:"", type:"", timeSpent:"", timeSpentUnit:"min",
};
const blankFilters = { userId:"", dateFrom:"", dateTo:"", type:"", status:"", search:"" };

const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString("en-GB", { day:"2-digit", month:"short", year:"numeric" });

/* ══════════════════════════════════════════════════════════════ */
const Tickets: React.FC = () => {
  const { user } = useAuth();
  const isAdmin   = user?.role === "ADMIN";
  const isManager = user?.role === "MANAGER";
  const canEditPerm   = usePermission("tickets", "edit");
  const canDeletePerm = usePermission("tickets", "delete");

  const [tickets,    setTickets]    = useState<any[]>([]);
  const [users,      setUsers]      = useState<any[]>([]);
  const [form,       setForm]       = useState({ ...blankForm });
  const [createOpen, setCreateOpen] = useState(false);
  const [editTicket, setEditTicket] = useState<any|null>(null);
  const [viewTicket, setViewTicket] = useState<any|null>(null);
  const [ticketSearch, setTicketSearch] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [showSearch, setShowSearch] = useState(false);
  const [participants, setParticipants] = useState<any[]>([]);
  const [logTicketOpen, setLogTicketOpen] = useState(false);
  const [logTicketForm, setLogTicketForm] = useState({ minutes: "", unit: "min", date: new Date().toISOString().slice(0,10), comment: "" });
  const [ticketWorkLogs, setTicketWorkLogs] = useState<any[]>([]);
  const [comments, setComments]     = useState<any[]>([]);
  const [newComment, setNewComment] = useState("");

  /* filters */
  const [filters,        setFilters]        = useState({ ...blankFilters });
  const [activeFilters,  setActiveFilters]  = useState({ ...blankFilters });
  const [filtersVisible, setFiltersVisible] = useState(false);

  /* ── load ──────────────────────────────────────────────────── */
  const load = () => {
    api.get("/tickets", { params: { parentId:"null" } }).then(r => setTickets(r.data));
  };
  useEffect(() => {
    load();
    api.get("/users").then(r => setUsers(r.data)).catch(()=>{});
  }, []);

  /* ── apply / clear ──────────────────────────────────────────── */
  const applyFilters  = () => setActiveFilters({ ...filters });
  const clearFilters  = () => { setFilters({ ...blankFilters }); setActiveFilters({ ...blankFilters }); };

  /* ── computed filtered list ─────────────────────────────────── */
  const filtered = useMemo(() => {
    return tickets.filter(t => {
      const af = filters;

      if (af.search) {
        const q = af.search.toLowerCase();
        const match = t.ticketNumber?.toLowerCase().includes(q) ||
                      t.referenceLink?.toLowerCase().includes(q);
        if (!match) return false;
      }
      if (af.status && t.status !== af.status) return false;
      if (af.type   && t.type   !== af.type)   return false;
      if (af.userId) {
        const byCreator  = t.createdBy?.id  === af.userId;
        const byAssigned = t.assignedTo?.id === af.userId;
        const byParticipant = t.participants?.some((p: any) => p.userId === af.userId);
        const byWorkLog = t.workLogs?.some((w: any) => w.employeeId === af.userId);
        if (!byCreator && !byAssigned && !byParticipant && !byWorkLog) return false;
      }
      if (af.dateFrom) {
        if (new Date(t.createdAt) < new Date(af.dateFrom)) return false;
      }
      if (af.dateTo) {
        const to = new Date(af.dateTo);
        to.setHours(23,59,59,999);
        if (new Date(t.createdAt) > to) return false;
      }
      return true;
    });
  }, [tickets, filters]);

  /* ── active filter chips ─────────────────────────────────────── */
  const activeChips = useMemo(() => {
    const chips: { key: string; label: string }[] = [];
    if (activeFilters.search)   chips.push({ key:"search",   label:`"${activeFilters.search}"` });
    if (activeFilters.status)   chips.push({ key:"status",   label:`Status: ${activeFilters.status.replace(/_/g," ")}` });
    if (activeFilters.type)     chips.push({ key:"type",     label:`Type: ${TYPE_LABEL[activeFilters.type]||activeFilters.type}` });
    if (activeFilters.userId) {
      const u = users.find(u => u.id === activeFilters.userId);
      if (u) chips.push({ key:"userId", label:`User: ${u.name}` });
    }
    if (activeFilters.dateFrom) chips.push({ key:"dateFrom", label:`From: ${activeFilters.dateFrom}` });
    if (activeFilters.dateTo)   chips.push({ key:"dateTo",   label:`To: ${activeFilters.dateTo}` });
    return chips;
  }, [activeFilters, users]);

  const removeChip = (key: string) => {
    setActiveFilters(f => ({ ...f, [key]:"" }));
    setFilters(f      => ({ ...f, [key]:"" }));
  };

  /* ── permissions ────────────────────────────────────────────── */
  const canEdit   = (t: any) => canEditPerm || t.createdBy?.id === user?.id;
  const canDelete = (t: any) => canDeletePerm;

  const effectiveDept = (f: typeof form) =>
    f.department === "Other" ? (f.customDepartment || "Other") : f.department;

  /* ── create ─────────────────────────────────────────────────── */
  const submitCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const mins = parseToMinutes(form.timeSpent, form.timeSpentUnit || "min");
    if (!mins || mins < 1) { toast.error("Minimum 1 minute"); return; }
    try {
      const { initialComment, ...ticketData } = form;
      const res = await api.post("/tickets", { ...ticketData, timeSpent: mins, department: effectiveDept(form) });
      if (initialComment?.trim() && res.data?.id) {
        await api.post(`/tickets/${res.data.id}/comments`, { content: initialComment }).catch(() => {});
      }
      toast.success("Ticket created!");
      setCreateOpen(false); setForm({ ...blankForm }); load();
    } catch(err: any) {
      const msg = err?.response?.data?.message || "Failed to create ticket";
      toast.error(msg);
    }
  };

  /* ── edit ───────────────────────────────────────────────────── */
  const openEdit = (t: any) => {
    const isOther = t.department && !DEPARTMENTS.slice(0,-1).includes(t.department);
    setEditTicket(t);
    setForm({
      ticketNumber:    t.ticketNumber    || "",
      referenceLink:   t.referenceLink   || "",
      initialComment:  "",
      department:      isOther ? "Other" : (t.department || ""),
      customDepartment:isOther ? t.department : "",
      type:            t.type            || "",
      timeSpent:       String(t.timeSpent ?? ""),
      timeSpentUnit:   "min",
    });
  };
  const submitEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    const mins = parseToMinutes(form.timeSpent, form.timeSpentUnit || "min");
    if (!mins || mins < 1) { toast.error("Minimum 1 minute"); return; }
    try {
      await api.put(`/tickets/${editTicket.id}`, { ...form, timeSpent: mins, department: effectiveDept(form) });
      toast.success("Ticket updated!");
      setEditTicket(null); setForm({ ...blankForm }); load();
    } catch(err: any) {
      const msg = err?.response?.data?.message || "Failed to update ticket";
      toast.error(msg);
    }
  };

  /* ── delete ─────────────────────────────────────────────────── */
  const deleteTicket = async (id: string) => {
    if (!window.confirm("Delete this ticket? This cannot be undone.")) return;
    try {
      await api.delete(`/tickets/${id}`);
      toast.success("Ticket deleted!"); load();
    } catch { toast.error("Failed to delete ticket"); }
  };

  /* ── view ticket + comments ──────────────────────────────────── */
  const openViewTicket = async (t: any) => {
    setViewTicket(t);
    loadParticipants(t.id);
    loadTicketWorkLogs(t.id);
    try {
      const r = await api.get(`/tickets/${t.id}/comments`);
      setComments(r.data);
    } catch { setComments([]); }
  };

  const submitComment = async () => {
    if (!newComment.trim() || !viewTicket) return;
    try {
      const r = await api.post(`/tickets/${viewTicket.id}/comments`, { content: newComment });
      setComments(prev => [...prev, r.data]);
      setNewComment("");
    } catch { toast.error("Failed to add comment"); }
  };

  const searchTicket = async (q: string) => {
    setTicketSearch(q);
    if (q.trim().length < 2) { setSearchResults([]); return; }
    try {
      const r = await api.get(`/tickets/search/${encodeURIComponent(q.trim())}`);
      setSearchResults(r.data);
    } catch { setSearchResults([]); }
  };

  const openSearchResult = async (t: any) => {
    setShowSearch(false); setTicketSearch(""); setSearchResults([]);
    await api.post(`/tickets/${t.id}/join`).catch(() => {});
    openViewTicket(t);
  };

  const loadParticipants = async (ticketId: string) => {
    try { const r = await api.get(`/tickets/${ticketId}/participants`); setParticipants(r.data); }
    catch { setParticipants([]); }
  };

  const loadTicketWorkLogs = async (ticketId: string) => {
    try { const r = await api.get(`/tickets/${ticketId}/work-logs`); setTicketWorkLogs(r.data); }
    catch { setTicketWorkLogs([]); }
  };

  const submitTicketLog = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!viewTicket) return;
    const mins = parseToMinutes(logTicketForm.minutes, logTicketForm.unit || "min");
    if (!mins || mins < 1) {}
    try {
      const _pc = await api.get(`/comment-policy/check?minutes=${mins}`);
      const _v: any = _pc.data || {};
      const _trim = (logTicketForm.comment || "").trim();
      if (_v.required && (!_trim || _trim.length < (_v.minLength || 0))) {
        toast.error(`Comment of ${_v.minLength}+ characters required for ${mins} min`); return;
      }
      if (!_v.required && _v.recommended && !_trim) {
        if (!window.confirm(`You worked ${mins} minutes — a short comment is recommended. Save without one?`)) return;
      }
    } catch {}
    if (!mins || mins < 1) { toast.error("Minimum 1 minute"); return; }
    try {
      await api.post(`/tickets/${viewTicket.id}/work-logs`, {
        minutes: mins, date: logTicketForm.date, comment: logTicketForm.comment,
      });
      toast.success("Time logged!");
      setLogTicketOpen(false);
      setLogTicketForm({ minutes: "", unit: "min", date: new Date().toISOString().slice(0,10), comment: "" });
      loadTicketWorkLogs(viewTicket.id);
      loadParticipants(viewTicket.id);
      load();
    } catch (_e) { toast.error((_e as any)?.response?.data?.message || "Failed to log time"); }
  };

  const addParticipant = async (userId: string) => {
    if (!viewTicket) return;
    try {
      await api.post(`/tickets/${viewTicket.id}/participants`, { userId });
      loadParticipants(viewTicket.id);
    } catch { toast.error("Failed to add participant"); }
  };

  const removeParticipant = async (userId: string) => {
    if (!viewTicket) return;
    try {
      await api.delete(`/tickets/${viewTicket.id}/participants/${userId}`);
      loadParticipants(viewTicket.id);
    } catch { toast.error("Failed to remove"); }
  };

  /* ── status ─────────────────────────────────────────────────── */
  const updateStatus = async (id: string, status: string) => {
    await api.put(`/tickets/${id}`, { status }); load();
  };

  /* ── ticket form (inline JSX) ───────────────────────────────── */
  const ticketFormJSX = (onSubmit: (e:React.FormEvent)=>void, label:string, onClose:()=>void, isCreate = false) => (
    <form onSubmit={onSubmit} className="p-5 space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Ticket Number *</label>
          <input value={form.ticketNumber}
            onChange={e => setForm(f=>({...f, ticketNumber:e.target.value}))} required
            placeholder="TKT-001"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"/>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
          <select value={form.type} onChange={e=>setForm(f=>({...f, type:e.target.value}))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none">
            <option value="">Select...</option>
            {TYPE_OPTIONS.map(t=><option key={t} value={t}>{TYPE_LABEL[t]}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Reference Link *</label>
        <input value={form.referenceLink}
          onChange={e=>setForm(f=>({...f, referenceLink:e.target.value}))} required
          placeholder="Enter related link (ticket / WhatsApp / system link)"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"/>
      </div>
      {isCreate && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Comment</label>
          <textarea value={form.initialComment} onChange={e=>setForm(f=>({...f, initialComment:e.target.value}))}
            rows={2} placeholder="Add an initial comment (optional)..."
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none resize-none"/>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
          <select value={form.department} onChange={e=>setForm(f=>({...f, department:e.target.value}))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none">
            <option value="">Select...</option>
            {DEPARTMENTS.map(d=><option key={d}>{d}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Time Spent *</label>
          <div className="flex gap-2">
            <input type="text"
              value={form.timeSpent}
              onChange={e=>setForm(f=>({...f, timeSpent:e.target.value}))} required
              placeholder={(form.timeSpentUnit||"min")==="hr" ? "e.g. 1.5" : "e.g. 30"}
              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"/>
            <div className="flex border border-gray-300 rounded-lg overflow-hidden text-sm">
              <button type="button" onClick={()=>setForm(f=>({...f,timeSpentUnit:"min"}))}
                className={`px-3 py-2 font-medium transition-colors ${(form.timeSpentUnit||"min")==="min"?"bg-blue-600 text-white":"bg-white text-gray-600 hover:bg-gray-50"}`}>min</button>
              <button type="button" onClick={()=>setForm(f=>({...f,timeSpentUnit:"hr"}))}
                className={`px-3 py-2 font-medium transition-colors ${form.timeSpentUnit==="hr"?"bg-blue-600 text-white":"bg-white text-gray-600 hover:bg-gray-50"}`}>hr</button>
            </div>
          </div>
          <p className="text-xs text-gray-400 mt-1">Also accepts: 1h 30m · 1.5 · 90</p>
        </div>
      </div>
      {form.department === "Other" && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Custom Department Name</label>
          <input value={form.customDepartment}
            onChange={e=>setForm(f=>({...f, customDepartment:e.target.value}))}
            placeholder="Enter department name"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"/>
        </div>
      )}
      <div className="flex gap-3 pt-2 border-t border-gray-100">
        <button type="button" onClick={onClose}
          className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-50 text-sm">Cancel</button>
        <button type="submit"
          className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 text-sm font-medium">{label}</button>
      </div>
    </form>
  );

  /* ── render ─────────────────────────────────────────────────── */
  return (
    <div className="space-y-4">

      {/* ── Header ─────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Tickets</h2>
        <div className="flex items-center gap-2">
          <button onClick={() => setFiltersVisible(v=>!v)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
              filtersVisible ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
            }`}>
            <Filter size={15}/> Filters
            {activeChips.length > 0 && (
              <span className="bg-white text-blue-600 text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                {activeChips.length}
              </span>
            )}
          </button>
          <button onClick={() => { setCreateOpen(true); setForm({...blankForm}); }}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm font-medium">
            <Plus size={15}/> New Ticket
          </button>
        </div>
      </div>

      {/* ── Filter Panel ────────────────────────────────────────── */}
      {filtersVisible && (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 space-y-4">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <Filter size={14}/> Filter Tickets
            </span>
            <button onClick={() => setFiltersVisible(false)}
              className="text-gray-400 hover:text-gray-600"><X size={16}/></button>
          </div>

          {/* Row 1: search + status + type */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
              <input value={filters.search}
                onChange={e=>setFilters(f=>({...f, search:e.target.value}))}
                placeholder="Search ticket #, link..."
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"/>
            </div>
            <select value={filters.status} onChange={e=>setFilters(f=>({...f, status:e.target.value}))}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none">
              <option value="">All Status</option>
              {STATUS_OPTIONS.map(s=><option key={s} value={s}>{s.replace(/_/g," ")}</option>)}
            </select>
            <select value={filters.type} onChange={e=>setFilters(f=>({...f, type:e.target.value}))}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none">
              <option value="">All Types</option>
              {TYPE_OPTIONS.map(t=><option key={t} value={t}>{TYPE_LABEL[t]}</option>)}
            </select>
          </div>

          {/* Row 2: user + date range */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <select value={filters.userId} onChange={e=>setFilters(f=>({...f, userId:e.target.value}))}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none">
              <option value="">All Users</option>
              {users.map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Calendar size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"/>
                <input type="date" value={filters.dateFrom}
                  onChange={e=>setFilters(f=>({...f, dateFrom:e.target.value}))}
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"/>
              </div>
              <span className="text-gray-400 text-xs shrink-0">to</span>
              <div className="relative flex-1">
                <Calendar size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"/>
                <input type="date" value={filters.dateTo}
                  onChange={e=>setFilters(f=>({...f, dateTo:e.target.value}))}
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"/>
              </div>
            </div>
            {/* Action buttons */}
            <div className="flex gap-2">
              <button onClick={applyFilters}
                className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 text-sm font-medium flex items-center justify-center gap-1.5">
                <Filter size={13}/> Apply Filters
              </button>
              <button onClick={clearFilters}
                className="px-3 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 text-sm flex items-center gap-1">
                <XCircle size={13}/> Reset
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Active Filter Chips ──────────────────────────────────── */}
      {activeChips.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-semibold text-gray-500 flex items-center gap-1">
            <Filter size={12}/> Active:
          </span>
          {activeChips.map(chip => (
            <span key={chip.key}
              className="flex items-center gap-1 bg-blue-50 text-blue-700 border border-blue-200 text-xs px-2.5 py-1 rounded-full font-medium">
              {chip.label}
              <button onClick={() => removeChip(chip.key)} className="hover:text-blue-900 ml-0.5">
                <X size={11}/>
              </button>
            </span>
          ))}
          <button onClick={clearFilters}
            className="text-xs text-gray-400 hover:text-red-500 flex items-center gap-1 ml-1">
            <XCircle size={12}/> Clear all
          </button>
          <span className="text-xs text-gray-400 ml-auto">{filtered.length} result{filtered.length!==1?"s":""}</span>
        </div>
      )}

      {/* ── Table ───────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[1060px]">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              {["Ticket #","Reference Link","Department","Size","Time Spent","Created By","Date","Status","Type","Actions"].map(h=>(
                <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.map(t => (
              <tr key={t.id} className="hover:bg-gray-50 transition-colors">
                <td className="px-4 py-3">
                  <button onClick={() => openViewTicket(t)} className="flex items-center gap-1 font-semibold text-blue-600 hover:underline">
                    <Hash size={12}/>{t.ticketNumber}
                  </button>
                </td>
                <td className="px-4 py-3 max-w-[160px] truncate" title={t.referenceLink}>
                  {t.referenceLink ? (
                    <a href={t.referenceLink.startsWith("http") ? t.referenceLink : `https://${t.referenceLink}`}
                      target="_blank" rel="noopener noreferrer"
                      className="text-blue-600 hover:underline text-xs flex items-center gap-1"
                      onClick={e => e.stopPropagation()}>
                      <ExternalLink size={12} /> Open
                    </a>
                  ) : <span className="text-gray-400">-</span>}
                </td>
                <td className="px-4 py-3">
                  {t.department
                    ? <span className="flex items-center gap-1 text-gray-600 text-xs"><Building2 size={11}/>{t.department}</span>
                    : <span className="text-gray-400">-</span>}
                </td>
                <td className="px-4 py-3">
                  <SizeBadge size={calcTicketSize(t)} compact />
                </td>
                <td className="px-4 py-3">
                  <span className="flex items-center gap-1 font-medium text-gray-700">
                    <Clock size={12} className="text-blue-500"/>{fmtMin(isAdmin ? (t.timeSpent ?? 0) : (t.workLogs || []).filter((w: any) => w.employeeId === user?.id).reduce((s: number, w: any) => s + (w.minutes || 0), 0))}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className="flex items-center gap-1 text-gray-600 text-xs">
                    <User size={11}/>{t.createdBy?.name || "—"}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className="flex items-center gap-1 text-gray-500 text-xs">
                    <Calendar size={11}/>{fmtDate(t.createdAt)}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[t.status]}`}>
                    {t.status.replace(/_/g," ")}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {t.type
                    ? <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${TYPE_COLORS[t.type]||"bg-gray-100 text-gray-600"}`}>{TYPE_LABEL[t.type]||t.type}</span>
                    : <span className="text-gray-400 text-xs">-</span>}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <select value={t.status} onChange={e=>updateStatus(t.id,e.target.value)}
                      className="text-xs border border-gray-200 rounded-lg px-2 py-1 focus:outline-none bg-white text-gray-600">
                      {STATUS_OPTIONS.map(s=><option key={s} value={s}>{s.replace(/_/g," ")}</option>)}
                    </select>
                    {canEdit(t) && (
                      <button onClick={()=>openEdit(t)}
                        className="flex items-center gap-1 text-xs bg-gray-50 text-gray-600 border border-gray-200 px-2.5 py-1 rounded-lg hover:bg-gray-100 font-medium">
                        <Pencil size={11}/> Edit
                      </button>
                    )}
                    {canDelete(t) && (
                      <button onClick={()=>deleteTicket(t.id)}
                        className="flex items-center gap-1 text-xs bg-red-50 text-red-500 border border-red-100 px-2.5 py-1 rounded-lg hover:bg-red-100 font-medium">
                        <Trash2 size={11}/> Delete
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="text-center py-14 text-gray-400">
            {activeChips.length > 0 ? "No tickets match the selected filters" : "No tickets found"}
          </div>
        )}
      </div>

      {/* ══ Create Modal ══════════════════════════════════════════ */}
      {createOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-lg my-4">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">New Ticket</h3>
              <button onClick={()=>setCreateOpen(false)} className="text-gray-400 hover:text-gray-600"><X size={20}/></button>
            </div>
            {ticketFormJSX(submitCreate, "Create Ticket", ()=>setCreateOpen(false), true)}
          </div>
        </div>
      )}

      {/* ══ Edit Modal ════════════════════════════════════════════ */}
      {editTicket && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-lg my-4">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Edit Ticket</h3>
                <p className="text-xs text-gray-500 mt-0.5">#{editTicket.ticketNumber}</p>
              </div>
              <button onClick={()=>setEditTicket(null)} className="text-gray-400 hover:text-gray-600"><X size={20}/></button>
            </div>
            {ticketFormJSX(submitEdit, "Save Changes", ()=>setEditTicket(null))}
          </div>
        </div>
      )}

      {/* View Ticket + Comments Modal */}
      {viewTicket && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-lg my-4 shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Hash size={16}/> {viewTicket.ticketNumber}
                </h3>
                <div className="flex items-center gap-2 mt-1">
                  <SizeBadge size={calcTicketSize(viewTicket)} />
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[viewTicket.status]}`}>{viewTicket.status}</span>
                  {viewTicket.type && <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${TYPE_COLORS[viewTicket.type]}`}>{TYPE_LABEL[viewTicket.type]}</span>}
                </div>
              </div>
              <button onClick={() => { setViewTicket(null); setComments([]); setNewComment(""); }} className="text-gray-400 hover:text-gray-600"><X size={20}/></button>
            </div>
            <div className="p-5 space-y-4">
              {/* Ticket info */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                {viewTicket.department && (
                  <div><span className="text-xs text-gray-400">Department</span><p className="text-gray-700">{viewTicket.department}</p></div>
                )}
                <div><span className="text-xs text-gray-400">{isAdmin ? "Total Time" : "My Time"}</span><p className="text-gray-700 font-medium">{fmtMin(isAdmin ? (viewTicket.timeSpent ?? 0) : (viewTicket.workLogs || []).filter((w: any) => w.employeeId === user?.id).reduce((s: number, w: any) => s + (w.minutes || 0), 0))}</p></div>
                {viewTicket.createdBy && (
                  <div><span className="text-xs text-gray-400">Created By</span><p className="text-gray-700">{viewTicket.createdBy.name}</p></div>
                )}
                <div><span className="text-xs text-gray-400">Date</span><p className="text-gray-700">{new Date(viewTicket.createdAt).toLocaleDateString()}</p></div>
              </div>
              {viewTicket.referenceLink && (
                <div>
                  <span className="text-xs text-gray-400">Reference Link</span>
                  <a href={viewTicket.referenceLink.startsWith("http") ? viewTicket.referenceLink : `https://${viewTicket.referenceLink}`}
                    target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-sm text-blue-600 hover:underline mt-0.5">
                    <ExternalLink size={12}/> Open Link
                  </a>
                </div>
              )}

              {/* Log Time Button */}
              <div className="border-t border-gray-100 pt-3">
                <button onClick={() => setLogTicketOpen(true)}
                  className="flex items-center gap-1.5 bg-blue-50 border border-blue-200 text-blue-700 px-3 py-1.5 rounded-lg text-sm hover:bg-blue-100 font-medium">
                  <Clock size={14}/> Log Time
                </button>
                {ticketWorkLogs.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {ticketWorkLogs.slice(0, 5).map((wl: any) => (
                      <div key={wl.id} className="flex items-center justify-between text-xs text-gray-500 bg-gray-50 rounded px-2 py-1">
                        <span className="font-medium text-gray-700">{wl.employee?.name}</span>
                        <span>{fmtMin(wl.minutes)} · {new Date(wl.date).toLocaleDateString("en-GB", { day:"2-digit", month:"short" })}</span>
                      </div>
                    ))}
                    {ticketWorkLogs.length > 5 && <p className="text-xs text-gray-400">+{ticketWorkLogs.length - 5} more</p>}
                  </div>
                )}
              </div>

              {/* Participants */}
              <div className="border-t border-gray-100 pt-3">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                    <UsersIcon size={14}/> Participants ({participants.length})
                  </h4>
                </div>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {viewTicket.assignedTo && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">
                      {viewTicket.assignedTo.name} (assigned)
                    </span>
                  )}
                  {participants.map((p: any) => (
                    <span key={p.id} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full font-medium flex items-center gap-1">
                      {p.user?.name}
                      <button onClick={() => removeParticipant(p.userId)} className="text-gray-400 hover:text-red-500">
                        <X size={10}/>
                      </button>
                    </span>
                  ))}
                  {participants.length === 0 && !viewTicket.assignedTo && (
                    <span className="text-xs text-gray-400">No participants</span>
                  )}
                </div>
                <select onChange={e => { if (e.target.value) { addParticipant(e.target.value); e.target.value = ""; } }}
                  className="border border-gray-300 rounded-lg px-2 py-1 text-xs w-full focus:ring-2 focus:ring-blue-500 outline-none">
                  <option value="">+ Add participant...</option>
                  {users.filter((u: any) => u.id !== viewTicket.assignedTo?.id && !participants.find((p: any) => p.userId === u.id)).map((u: any) => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
              </div>

              {/* Comments */}
              <div className="border-t border-gray-100 pt-3">
                <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-1.5 mb-3">
                  <MessageSquare size={14}/> Comments ({comments.length})
                </h4>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {comments.length === 0 && <p className="text-xs text-gray-400 text-center py-4">No comments yet</p>}
                  {comments.map((c: any) => (
                    <div key={c.id} className="bg-gray-50 rounded-lg px-3 py-2">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-semibold text-gray-700">{c.author?.name}</span>
                        <span className="text-xs text-gray-400">{new Date(c.createdAt).toLocaleString("en-GB", { day:"2-digit", month:"short", hour:"2-digit", minute:"2-digit" })}</span>
                      </div>
                      <p className="text-sm text-gray-600">{c.content}</p>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 mt-3">
                  <input value={newComment} onChange={e => setNewComment(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submitComment(); } }}
                    placeholder="Add a comment..."
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"/>
                  <button onClick={submitComment} disabled={!newComment.trim()}
                    className="bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-40 transition-colors">
                    <Send size={16}/>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Log Time on Ticket Modal */}
      {logTicketOpen && viewTicket && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Log Time</h3>
                <p className="text-xs text-gray-500 mt-0.5">Ticket #{viewTicket.ticketNumber}</p>
              </div>
              <button onClick={() => setLogTicketOpen(false)} className="text-gray-400 hover:text-gray-600"><X size={20}/></button>
            </div>
            <form onSubmit={submitTicketLog} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Time Logged *</label>
                <div className="flex gap-2">
                  <input type="text" value={logTicketForm.minutes}
                    onChange={e => setLogTicketForm(f => ({...f, minutes: e.target.value}))}
                    required placeholder={(logTicketForm.unit||"min")==="hr" ? "e.g. 1.5" : "e.g. 30"}
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none"/>
                  <div className="flex border border-gray-300 rounded-lg overflow-hidden text-sm">
                    <button type="button" onClick={() => setLogTicketForm(f=>({...f,unit:"min"}))}
                      className={`px-3 py-2 font-medium transition-colors ${(logTicketForm.unit||"min")==="min"?"bg-blue-600 text-white":"bg-white text-gray-600 hover:bg-gray-50"}`}>min</button>
                    <button type="button" onClick={() => setLogTicketForm(f=>({...f,unit:"hr"}))}
                      className={`px-3 py-2 font-medium transition-colors ${logTicketForm.unit==="hr"?"bg-blue-600 text-white":"bg-white text-gray-600 hover:bg-gray-50"}`}>hr</button>
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-1">Also accepts: 1h 30m · 1.5 · 90</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input type="date" value={logTicketForm.date} onChange={e => setLogTicketForm(f => ({...f, date: e.target.value}))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none"/>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Note (optional)</label>
                <textarea value={logTicketForm.comment} onChange={e => setLogTicketForm(f => ({...f, comment: e.target.value}))}
                  rows={2} placeholder="What did you work on?"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none resize-none"/>
                <CommentPolicyHint minutes={parseToMinutes(logTicketForm.minutes, logTicketForm.unit || "min") || 0} comment={logTicketForm.comment} />
              </div>
              <div className="flex gap-3 pt-1 border-t border-gray-100">
                <button type="button" onClick={() => setLogTicketOpen(false)}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-50 text-sm">Cancel</button>
                <button type="submit"
                  className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 text-sm font-medium">Log Time</button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default Tickets;
